import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button,
    TextField,
    Grid,
    makeStyles,
} from '@material-ui/core';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import I18n from 'i18n-js';
import { InvoiceCreateModel } from 'api/models';

const EMPTY_VALUES: InvoiceCreateModel = {
    companyName: '',
    contactName: '',
    addressLine: '',
    zip: '',
    city: '',
};

const validationSchema = Yup.object().shape({
    companyName: Yup.string().trim().required(),
    contactName: Yup.string().trim().required(),
    addressLine: Yup.string().trim().required(),
    zip: Yup.string()
        .trim()
        .required()
        .matches(/^\d{4}$/, I18n.t('form.errors.zipCode')),
    city: Yup.string().trim().required(),
});

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: 0,
    },
    subtitle: {
        marginBottom: theme.spacing(2),
    },
    actions: {
        marginTop: 16,
        paddingRight: 24,
        paddingLeft: 24,
    },
}));

export interface CreateInvoiceModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (values: InvoiceCreateModel) => void;
}

function InvoiceDetailsModal({ open, onClose, onSubmit }: CreateInvoiceModalProps) {
    const classes = useStyles();

    const handleSubmit = async (values: InvoiceCreateModel) => {
        onSubmit(values);
    };

    function fieldLabel(key: string, isError: boolean = false) {
        const fieldName = I18n.t(key);
        return isError ? I18n.t('form.errors.required', { name: fieldName }) : fieldName;
    }

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="xs"
            scroll={'paper'}
            aria-labelledby="create-invoice-dialog-title"
        >
            <Formik
                initialValues={EMPTY_VALUES}
                validationSchema={validationSchema}
                validateOnMount
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    isValid,
                    dirty,
                    isSubmitting,
                }) => (
                    <Form style={{ display: 'contents' }}>
                        <DialogTitle disableTypography className={classes.dialogTitle}>
                            <h2>{I18n.t('form.createInvoiceModalTitle')}</h2>
                        </DialogTitle>

                        <DialogContent>
                            <DialogContentText className={classes.subtitle}>
                                {I18n.t('form.createInvoiceModalSubtitle')}
                            </DialogContentText>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="companyName"
                                        name="companyName"
                                        label={fieldLabel(
                                            'form.companyName',
                                            touched.companyName && Boolean(errors.companyName)
                                        )}
                                        placeholder={I18n.t('form.companyName')}
                                        variant="outlined"
                                        value={values.companyName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.companyName && Boolean(errors.companyName)}
                                        helperText={touched.companyName && errors.companyName}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="contactName"
                                        name="contactName"
                                        label={fieldLabel(
                                            'form.contactName',
                                            touched.contactName && Boolean(errors.contactName)
                                        )}
                                        placeholder={I18n.t('form.contactName')}
                                        variant="outlined"
                                        value={values.contactName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.contactName && Boolean(errors.contactName)}
                                        helperText={touched.contactName && errors.contactName}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="addressLine"
                                        name="addressLine"
                                        label={fieldLabel(
                                            'form.restaurantAddress',
                                            touched.addressLine && Boolean(errors.addressLine)
                                        )}
                                        placeholder={I18n.t('form.restaurantAddress')}
                                        variant="outlined"
                                        value={values.addressLine}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.addressLine && Boolean(errors.addressLine)}
                                        helperText={touched.addressLine && errors.addressLine}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="zip"
                                        name="zip"
                                        label={fieldLabel(
                                            'form.zip',
                                            touched.zip && Boolean(errors.zip)
                                        )}
                                        placeholder={I18n.t('form.zip')}
                                        variant="outlined"
                                        inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                                        value={values.zip}
                                        onChange={(e) =>
                                            setFieldValue(
                                                'zip',
                                                e.target.value.replace(/\D/g, '').slice(0, 4)
                                            )
                                        }
                                        onBlur={handleBlur}
                                        error={touched.zip && Boolean(errors.zip)}
                                        helperText={touched.zip && errors.zip}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="city"
                                        name="city"
                                        label={fieldLabel(
                                            'form.cityAsLocation',
                                            touched.city && Boolean(errors.city)
                                        )}
                                        placeholder={I18n.t('form.cityAsLocation')}
                                        variant="outlined"
                                        value={values.city}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.city && Boolean(errors.city)}
                                        helperText={touched.city && errors.city}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>

                        <DialogActions className={classes.actions}>
                            <Button
                                onClick={onClose}
                                disabled={isSubmitting}
                                variant="text"
                                color="primary"
                            >
                                {I18n.t('button.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={!isValid || !dirty || isSubmitting}
                            >
                                {I18n.t('button.createInvoice')}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}

export default InvoiceDetailsModal;
